import type { OnboardingIdType } from '~/features/onboarding/OnboardingIdType';

import { BaseLoadingContentProps } from '../../types/BaseLoadingContentProps';
import { UserAction } from '../../types/UserAction';
import { Actions } from '../Actions/Actions';
import { Box } from '../Box/Box';
import { Breadcrumbs } from '../Breadcrumbs/Breadcrumbs';
import { ContentContainer } from '../ContentContainer/ContentContainer';
import { DescriptionText } from '../DescriptionText/DescriptionText';
import { Layout } from '../Layout/Layout';
import { Logo } from '../Logo/Logo';
import { TruncateText } from '../Text/subcomponents/TruncateText';
import { Text } from '../Text/Text';

export interface WelcomeShellProps {
  /** Page title */
  title: string;
  /** Page subtitle, rendered between the title and description */
  subtitle?: string;
  /** Page description, rendered below the subtitle */
  description?: string;
  /** Breadcrumb-style nav actions, rendered above the title (e.g. a wizard's steps) */
  nav?: { actions: UserAction[] };
  /** Page actions, rendered below the content */
  actions?: UserAction[];
  /** Loading details for the content area */
  loadingContent?: BaseLoadingContentProps;
  /** Main content, rendered above any `onboardingIds` cards */
  content?: React.ReactNode;
  /** Renders an empty card per id, for an onboarding tour to target */
  onboardingIds?: OnboardingIdType[];
  /** Shows the Evisort logo above the nav/title */
  enableLogo?: boolean;
}

/**
 * A centered, full-page shell for onboarding/welcome-style content — logo,
 * title, subtitle/description, content, and page actions, all center-aligned.
 * Extracted from EDS 1.0 `PageLayout`'s `welcome` mode so callers can use it
 * standalone (e.g. inside a `Modal`, or alongside EDS 2.0 `PageLayout`)
 * without pulling in the rest of `PageLayout`'s API.
 */
export const WelcomeShell = ({
  actions = [],
  content,
  description,
  enableLogo,
  loadingContent,
  nav,
  onboardingIds,
  subtitle,
  title,
}: WelcomeShellProps) => {
  const pageTitle = (
    <TruncateText as="h1" m={0} textAlign="center" variant="title">
      {title}
    </TruncateText>
  );

  const pageDescription = description && (
    <DescriptionText text={description} textAlign="center" />
  );

  const pageSubtitle = subtitle && (
    <Text align="center" variant="body-bold">
      {subtitle}
    </Text>
  );

  const pageNav = nav && (
    <Layout
      align="center"
      alignSelf="flex-start"
      justify="space-between"
      ml={-4}
      spacing={4}
      w="100%"
    >
      <Breadcrumbs actions={nav.actions} />
    </Layout>
  );

  return (
    <Layout as="header" align="center" direction="column" px={8} spacing={12}>
      {enableLogo && <Logo />}
      {pageNav}
      <Layout direction="column" py={4} spacing={4}>
        {pageTitle}
        {pageSubtitle}
        {pageDescription}
      </Layout>
      <ContentContainer loadingContent={loadingContent}>
        <>
          {content}
          {onboardingIds && (
            <Layout preset="cards">
              {onboardingIds.map((onboardingId) => (
                <Box
                  key={onboardingId}
                  id={onboardingId}
                  p={4}
                  minW={300}
                  minH={360}
                />
              ))}
            </Layout>
          )}
          <Actions actions={actions} />
        </>
      </ContentContainer>
    </Layout>
  );
};
